import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { Button, Field, Modal, Select, Toast } from '@/components/ui';
import { dialerOptions, useDialers } from '@/lib/dialers';

interface Bot {
  id: string;
  name: string;
  code: string;
}

interface Result {
  assigned: string[];
  assigned_count: number;
  bot_name: string;
}

/**
 * Point many Vicidial agents at one bot.
 *
 * Ten agents created in one go needed ten trips through the edit form, and an
 * agent with no bot is not an error anyone sees — the call arrives and nothing
 * answers it.
 */
export function AssignBotToAgents({ bots }: { bots: Bot[] }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const [botId, setBotId] = useState('');
  const [integrationId, setIntegrationId] = useState('');
  const [scope, setScope] = useState<'unassigned' | 'all'>('unassigned');

  const { data: dialers } = useDialers();
  const servers = dialerOptions(dialers?.data);

  const assign = useMutation({
    mutationFn: () =>
      api.post<Result>('/vicidial-users/assign-bot', {
        bot_id: botId,
        integration_id: integrationId || undefined,
        only_unassigned: scope === 'unassigned',
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['vicidial-users'] });
      setOpen(false);
      setToast({
        message: result.assigned_count
          ? `Assigned ${result.bot_name} to ${result.assigned_count} agent${result.assigned_count === 1 ? '' : 's'}.`
          : 'No agents matched — they may all be assigned already.',
        tone: result.assigned_count ? 'good' : 'bad',
      });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not assign the bot'), tone: 'bad' }),
  });

  return (
    <>
      <Button variant="secondary" onClick={() => setOpen(true)}>
        Assign bot
      </Button>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Assign a bot to agents"
        description="Sets the primary bot on many agents at once."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button loading={assign.isPending} disabled={!botId} onClick={() => assign.mutate()}>
              Assign
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Bot" required hint="Every matching agent gets this as their primary bot.">
            <Select value={botId} onChange={(e) => setBotId(e.target.value)}>
              <option value="">Select…</option>
              {bots.map((bot) => (
                <option key={bot.id} value={bot.id}>
                  {bot.name} ({bot.code})
                </option>
              ))}
            </Select>
          </Field>

          {servers.length > 0 && (
            <Field label="Vicidial server" hint="Leave blank to cover every server.">
              <Select value={integrationId} onChange={(e) => setIntegrationId(e.target.value)}>
                <option value="">All servers</option>
                {servers.map((server) => (
                  <option key={server.value} value={server.value}>
                    {server.label}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field
            label="Which agents"
            hint="Overwriting is deliberate: it would otherwise silently undo assignments somebody made on purpose."
          >
            <Select
              value={scope}
              onChange={(e) => setScope(e.target.value as 'unassigned' | 'all')}
            >
              <option value="unassigned">Only agents with no bot yet</option>
              <option value="all">All agents — overwrite existing assignments</option>
            </Select>
          </Field>

          {bots.length === 0 && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-200">
              There are no active bots yet. Create one on the Bots page first.
            </p>
          )}
        </div>
      </Modal>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </>
  );
}
